import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { formatISODateRelative } from "lib/formatISODate";
import { useNotificationClick } from "lib/handleNotificationClick";
import { trpc } from "lib/trpc";
import { ClientNotification } from "@compass/shared/types";

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
    isSidebarCollapsed: boolean;
    isSocialPage?: boolean;
}

export const NotificationsModal = ({
    isOpen,
    onClose,
    isSidebarCollapsed,
    isSocialPage,
}: NotificationsModalProps) => {
    const onNotificationClick = useNotificationClick();
    const [notifications] = trpc.notifications.latest.useSuspenseQuery();
    const [activeTab, setActiveTab] = useState<"all" | "inbox" | "discussions" | "new">("all");

    // const privateTabs = ["All", "Discussions", "New"] as const;
    // const socialTabs = ["All", "Inbox", "New"] as const;
    // const notificationTabs = isSocialPage ? socialTabs : privateTabs;

    const inboxCount = notifications.filter((n) => n.data.type === "message").length;
    const discussionsCount = notifications.filter((n) => n.data.type === "discussion").length;
    const newCount = notifications.filter((n) => !n.isRead).length;

    const filteredNotifications = notifications.filter((n) => {
        if (activeTab === "inbox") return n.data.type === "message";
        if (activeTab === "discussions") return n.data.type === "discussion";
        if (activeTab === "new") return !n.isRead;
        return true; // all
    });

    const handleNotification = (notification: ClientNotification) => {
        onNotificationClick(notification);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-[#424242]/75" />
            <Dialog.Content className="fixed left-1/2 top-[10vh] z-50 flex max-h-[80vh] w-[calc(100vw-2rem)] max-w-[520px] -translate-x-1/2 flex-col rounded-2xl bg-white p-4 transition-all duration-300 sm:top-24">
                <div className="flex items-center justify-between">
                    <Dialog.Title className="font-sans text-[20px] font-medium leading-normal text-[#424242]">
                        Notifications
                    </Dialog.Title>
                    <Dialog.Close asChild>
                        <button className="ml-auto" aria-label="Close">
                            <CloseIcon className="h-[30px] w-[30px]" />
                        </button>
                    </Dialog.Close>
                </div>

                <div className="-mx-[16px] mt-4 flex flex-wrap gap-y-2 rounded-2xl border-b border-[#d4d5d5] px-4">
                    {[
                        {
                            key: "all",
                            label: "All",
                            count: notifications.length,
                        },
                        {
                            key: "inbox",
                            label: "Inbox",
                            count: inboxCount,
                        },
                        {
                            key: "discussions",
                            label: "Discussions",
                            count: discussionsCount,
                        },
                        {
                            key: "new",
                            label: "New",
                            count: newCount,
                        },
                    ].map((tab) => {
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key as any)}
                                className={`mr-6 flex items-center pb-2 text-sm font-medium focus:outline-none focus:ring-0 ${
                                    activeTab === tab.key
                                        ? "border-b-2 border-[#08abf2] font-semibold text-[#37598A]"
                                        : "text-gray-600 hover:text-gray-800"
                                }`}
                            >
                                {tab.label}
                                {tab.count > 0 && (
                                    <span
                                        className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold text-white ${
                                            activeTab === tab.key
                                                ? "bg-[#08abf2]"
                                                : "bg-[#08abf2] opacity-80"
                                        }`}
                                    >
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="-mx-[16px] flex min-h-0 flex-1 flex-col space-y-0 overflow-y-auto">
                    {filteredNotifications.length > 0 ? (
                        filteredNotifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`${notification.isRead ? "bg-[#ffffff]" : "bg-[#f5f5f5]"} relative flex cursor-pointer items-center justify-between rounded-none border-b border-[#d4d5d5] p-4 text-sm shadow-none hover:bg-[#f5f5f5]`}
                                onClick={() => handleNotification(notification)}
                            >
                                <div>
                                    <p className="text-gray-800">{notification.data.message}</p>
                                    <p className="text-xs text-gray-500">
                                        {formatISODateRelative(notification.createdAt)}
                                    </p>
                                </div>
                                {!notification.isRead && (
                                    <div className="bg-brand-atlantic absolute right-5 top-4 h-3 w-3 rounded-full"></div>
                                )}
                            </div>
                        ))
                    ) : (
                        <p className="mt-6 text-center text-gray-500">No notifications</p>
                    )}
                </div>
            </Dialog.Content>
        </Dialog.Root>
    );
};
